// The Markdown Help articles use (HelpView.swift's `HelpBlock.parse`, ported): headings, paragraphs, lists, notes,
// tables and code blocks, with inline emphasis, code and links. Pure data: the viewer builds DOM from it with React
// (no HTML string is ever parsed), so nothing in an article can run.
export type HelpBlock =
  | { type: 'title'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'subheading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'bullets'; items: string[] }
  | { type: 'numbered'; items: string[] }
  | { type: 'note'; text: string }
  | { type: 'table'; rows: string[][] }
  | { type: 'code'; text: string };

function listItem(line: string, numbered: boolean): string | null {
  if (!numbered) return line.startsWith('- ') ? line.slice(2) : null;
  const m = /^(\d+)\. (.*)$/.exec(line);
  return m ? m[2] : null;
}

export function parseBlocks(markdown: string): HelpBlock[] {
  const lines = markdown.split('\n');
  const blocks: HelpBlock[] = [];
  let i = 0;
  const take = (keep: (t: string) => boolean, strip: (t: string) => string) => {
    const taken: string[] = [];
    while (i < lines.length && keep(lines[i].trim())) taken.push(strip(lines[i++].trim()));
    return taken.join(' ');
  };
  const items = (first: string, numbered: boolean) => {
    i++;
    const out = [first];
    while (i < lines.length) {
      const trimmed = lines[i].trim();
      const item = listItem(trimmed, numbered);
      if (item !== null) out.push(item);
      else if (trimmed && lines[i].startsWith('  ')) out[out.length - 1] += ` ${trimmed}`;
      else break;
      i++;
    }
    return out;
  };
  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (!trimmed) {
      i++;
      continue;
    }
    if (trimmed.startsWith('```')) {
      i++;
      const code: string[] = [];
      while (i < lines.length && !lines[i].startsWith('```')) code.push(lines[i++]);
      if (i < lines.length) i++;
      blocks.push({ type: 'code', text: code.join('\n') });
    } else if (trimmed.startsWith('### ')) {
      blocks.push({ type: 'subheading', text: trimmed.slice(4) });
      i++;
    } else if (trimmed.startsWith('## ')) {
      blocks.push({ type: 'heading', text: trimmed.slice(3) });
      i++;
    } else if (trimmed.startsWith('# ')) {
      blocks.push({ type: 'title', text: trimmed.slice(2) });
      i++;
    } else if (trimmed.startsWith('>')) {
      blocks.push({ type: 'note', text: take((t) => t.startsWith('>'), (t) => t.slice(1).trim()) });
    } else if (trimmed.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const cells = lines[i++]
          .trim()
          .replace(/^\|+|\|+$/g, '')
          .split('|')
          .map((c) => c.trim());
        if (cells.every((c) => /^[-: ]*$/.test(c))) continue; // the header rule
        rows.push(cells);
      }
      blocks.push({ type: 'table', rows });
    } else if (listItem(trimmed, false) !== null) {
      blocks.push({ type: 'bullets', items: items(listItem(trimmed, false)!, false) });
    } else if (listItem(trimmed, true) !== null) {
      blocks.push({ type: 'numbered', items: items(listItem(trimmed, true)!, true) });
    } else {
      blocks.push({
        type: 'paragraph',
        text: take(
          (t) => !!t && !t.startsWith('#') && !t.startsWith('>') && !t.startsWith('|') && !t.startsWith('```') && listItem(t, false) === null && listItem(t, true) === null,
          (t) => t,
        ),
      });
    }
  }
  return blocks;
}

export type Inline =
  | { type: 'text'; text: string }
  | { type: 'strong'; children: Inline[] }
  | { type: 'em'; children: Inline[] }
  | { type: 'code'; text: string }
  | { type: 'link'; href: string; children: Inline[] };

/** Inline Markdown: `**strong**`, `*em*` / `_em_`, `` `code` ``, `[text](href)`, `\` escapes. Anything else is text. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let buffer = '';
  const flush = () => {
    if (buffer) out.push({ type: 'text', text: buffer });
    buffer = '';
  };
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (c === '\\' && i + 1 < text.length && /[\\`*_[\]()#>|-]/.test(text[i + 1])) {
      buffer += text[i + 1];
      i += 2;
      continue;
    }
    if (c === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > i) {
        flush();
        out.push({ type: 'code', text: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (c === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end > i + 2) {
        flush();
        out.push({ type: 'strong', children: parseInline(text.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }
    if ((c === '*' || c === '_') && text[i + 1] !== c && text[i + 1] !== ' ' && text[i + 1] !== undefined) {
      // `_` only at a word's start (snake_case stays text).
      if (c === '*' || i === 0 || !/[\p{L}\p{N}]/u.test(text[i - 1])) {
        let end = i + 1;
        while ((end = text.indexOf(c, end)) > 0 && (text[end - 1] === ' ' || text[end + 1] === c)) end++;
        if (end > i + 1 && (c === '*' || !/[\p{L}\p{N}]/u.test(text[end + 1] ?? ''))) {
          flush();
          out.push({ type: 'em', children: parseInline(text.slice(i + 1, end)) });
          i = end + 1;
          continue;
        }
      }
    }
    if (c === '[') {
      const close = findClose(text, i);
      if (close > 0 && text[close + 1] === '(') {
        const end = text.indexOf(')', close + 2);
        if (end > close) {
          flush();
          out.push({ type: 'link', href: text.slice(close + 2, end).trim(), children: parseInline(text.slice(i + 1, close)) });
          i = end + 1;
          continue;
        }
      }
    }
    buffer += c;
    i++;
  }
  flush();
  return out;
}

/** The `]` closing the `[` at `open` (nested brackets counted). */
function findClose(text: string, open: number): number {
  let depth = 0;
  for (let i = open; i < text.length; i++) {
    if (text[i] === '\\') {
      i++;
      continue;
    }
    if (text[i] === '[') depth++;
    else if (text[i] === ']' && --depth === 0) return i;
  }
  return -1;
}

/** Where a link goes: another article (`help:<id>`), a web page (http/https/mailto), or nowhere (anything else). */
export function linkTarget(href: string): { kind: 'article'; id: string } | { kind: 'web'; href: string } | null {
  const help = /^help:([a-z0-9-]+)$/.exec(href);
  if (help) return { kind: 'article', id: help[1] };
  if (/^(https?:|mailto:)/i.test(href)) return { kind: 'web', href };
  return null;
}

/** An article's plain text (for search and tests). */
export function inlineText(nodes: Inline[]): string {
  return nodes.map((n) => (n.type === 'text' || n.type === 'code' ? n.text : inlineText(n.children))).join('');
}
